import type { NextPage } from "next";
import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import Layout from "../../components/Layout";
import useStore from "../../store/store";
import { PencilIcon, TrashIcon } from "@heroicons/react/solid";
import DeleteEmployeeModal from "../../components/DeleteEmployeeModal";
import { fuzzyMatch } from "../../utils";
import Spinner from "../../components/Spinner";
import { routes } from "../../utils/constants";
import { PlusIcon } from "@heroicons/react/solid";
import { emptyForm } from "./register";
import ReactTooltip from "react-tooltip";
import dateToLocaleString from "../../utils/dateToLocaleString";

const EmployeePage: NextPage = () => {
  const { employees, dataLoaded, deleteAllEmployees, setLoading } = useStore();

  const [employeeToDelete, setEmployeeToDelete] = useState("");
  const [search, setSearch] = useState("");
  const [filteredEmployees, setFilteredEmployees] = useState(employees);

  useEffect(() => {
    setFilteredEmployees(
      employees.filter((emp) =>
        fuzzyMatch(search, emp.firstName + emp.lastName)
      )
    );
  }, [search, employees]);

  const exportEmployees = useCallback(() => {
    let headers: string[] = [];
    for (let header in emptyForm) {
      headers.push(header);
    }
    let csv = headers.join(";");

    for (let index = 0; index < employees.length; index++) {
      const employeeData: string[] = [];
      const employee = employees[index];
      for (let header in emptyForm) {
        //@ts-ignore
        employeeData.push(employee[header]);
      }

      csv += "\n" + employeeData.join(";");
    }

    var blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    var link = document.createElement("a");

    if (link.download !== undefined) {
      var url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "employees.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [employees]);

  if (!dataLoaded) {
    return (
      <Layout currentPage="employees">
        <Spinner></Spinner>
      </Layout>
    );
  }
  if (dataLoaded && employees.length === 0) {
    return (
      <Layout currentPage="employees">
        <div className="flex w-full justify-center items-center">
          <div className="mt-[10vh] flex flex-col justify-center items-center">
            <p>
              We vonden nog geen medewerkers voor jou...<br></br> klik hieronder
              om je eerste toe te voegen
            </p>
            <div className="mt-5">
              <Link href={routes.employeesRegister}>
                <a>
                  <button
                    type="button"
                    className="p-2.5 flex items-center bg-red rounded-lg text-white font-bold text-xs uppercase"
                  >
                    <PlusIcon height={20} className="mr-1"></PlusIcon>
                    Medewerker inschrijven
                  </button>
                </a>
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }
  return (
    <Layout currentPage="employees">
      <div className="container mt-10 mx-auto text-xs ">
        <div className="flex justify-between">
          <div>
            <label htmlFor="search"></label>
            Filter op naam:{" "}
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              id="search"
              name="search"
              type="search"
              className="leading-tight py-1 px-0 form-input appearance-none border-0 border-b text-xs focus:outline-none focus:shadow"
            />
          </div>
          <div>
            <button className="btn-secondary" onClick={exportEmployees}>
              Export data naar csv
            </button>
            {/* <button
              className="ml-3 btn-secondary text-red"
              onClick={() => {
                setLoading(true);
                deleteAllEmployees();
              }}
            >
              Delete all
            </button> */}
          </div>
        </div>
        <table className="text-xs mx-auto w-full">
          <thead className="text-left text-light-gray font-semibold border-b border-light-gray">
            <tr>
              <th className="py-4 pl-2">Naam</th>
              <th className="py-4 pl-2">E-mailadres</th>
              <th className="py-4 pl-2">Telefoonnr</th>
              <th className="py-4 pl-2">Postcode</th>
              <th className="py-4 pl-2">Stad</th>
              <th className="py-4 pl-2">RRN</th>
              <th className="py-4 pl-2">Datum ingeschreven</th>
            </tr>
          </thead>
          <tbody>
            {filteredEmployees
              .filter((x) => x && x.firstName)
              .map((employee) => {
                return (
                  <tr key={employee._id}>
                    <td className="truncate w-56 font-bold text-dark-grey py-4 pl-2">
                      {employee.firstName} {employee.lastName}
                    </td>
                    <td className="truncate w-56 text-dark-grey font-normal py-4 pl-2">
                      {employee.email}
                    </td>
                    <td className="truncate w-56 text-dark-grey font-normal py-4 pl-2">
                      {employee.phone}
                    </td>
                    <td className="truncate w-28 text-dark-grey font-normal py-4 pl-2">
                      {employee.zip}
                    </td>
                    <td className="truncate w-28 text-dark-grey font-normal py-4 pl-2">
                      {employee.city}
                    </td>
                    <td className="truncate w-56 text-dark-grey font-normal py-4 pl-2">
                      {employee.registerNumber}
                    </td>
                    <td className="truncate w-56 text-dark-grey font-normal flex justify-between align-center py-4 pl-2">
                      <div className="flex items-center">
                        <p className="align-middle">
                          {dateToLocaleString(employee.dateOfregistration)}
                        </p>
                      </div>
                      <div className="mr-3 text-2xl flex items-center">
                        <Link href={`/employees/${employee._id}`}>
                          <a className="flex items-center">
                            <button
                              className="text-red"
                              data-for="buttonTitle"
                              data-tip="Medewerker aanpassen"
                            >
                              <PencilIcon height={20}></PencilIcon>
                            </button>
                          </a>
                        </Link>
                        <button
                          className="text-red ml-5"
                          data-for="buttonTitle"
                          data-tip="Medewerker verwijderen"
                          onClick={() => {
                            document
                              .getElementById("deleteEmployeeModal")
                              // @ts-ignore
                              ?.showModal();
                            setEmployeeToDelete(employee._id!);
                          }}
                        >
                          <TrashIcon height={20}></TrashIcon>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
        {search && filteredEmployees.length === 0 && (
          <div className="w-full h-full flex justify-center items-center">
            <div className="m-20 flex justify-center items-center flex-col">
              <div>Geen medewerkers teruggevonden.</div>

              {!!search && (
                <button
                  className="btn-secondary mt-3"
                  onClick={() => setSearch("")}
                >
                  Maak filter leeg
                </button>
              )}
            </div>
          </div>
        )}
        <ReactTooltip
          id="buttonTitle"
          place="left"
          type="dark"
          effect="solid"
          multiline={true}
        />
      </div>
      <DeleteEmployeeModal employeeId={employeeToDelete}></DeleteEmployeeModal>
    </Layout>
  );
};

export default EmployeePage;
